import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CANDIDATE_LIMITS,
  RESUME_MAX_BYTES,
  STAGE_LABELS,
  STAGE_ORDER,
  languageLabel,
  recommendedStructure,
} from '@ai-interview/shared';
import type { InterviewStatus, Job, JobStatus, ResumeBriefRequest } from '@ai-interview/shared';
import { api } from '../lib/api';
import { usePagedList } from '../lib/usePagedList';
import { InviteLink } from '../components/InviteLink';
import { LoadMore } from '../components/LoadMore';
import { RoleForm } from '../components/RoleForm';
import {
  DecisionBadge,
  ErrorNotice,
  JobStatusBadge,
  RecommendationBadge,
  StatusBadge,
} from '../components/Status';

/** Candidates whose link can no longer be used: the row's action is to give them a new one. */
const NEEDS_NEW_LINK: InterviewStatus[] = ['expired', 'abandoned', 'revoked'];

export function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job>();
  const [jobError, setJobError] = useState<unknown>();
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    api.getJob(jobId).then(
      (loaded) => {
        setJob(loaded);
        setJobError(undefined);
      },
      (err: unknown) => setJobError(err),
    );
  }, [jobId]);

  const fetchCandidates = useCallback(
    (cursor?: string) =>
      jobId ? api.listInterviews(jobId, cursor) : Promise.resolve({ items: [], cursor: undefined }),
    [jobId],
  );
  const candidates = usePagedList(fetchCandidates);

  if (!job && !jobError) return <p className="muted">Loading role…</p>;

  return (
    <div className="stack">
      <ErrorNotice error={jobError ?? candidates.error} />

      {job ? (
        <>
          <div className="between">
            <div>
              <div className="row" style={{ gap: '0.6rem' }}>
                <h1 style={{ margin: 0 }}>{job.title}</h1>
                <JobStatusBadge status={job.status} />
              </div>
              <p className="muted" style={{ margin: '0.4rem 0 0' }}>
                {job.department ? `${job.department} · ` : ''}
                {job.seniority}
                {job.location ? ` · ${job.location}` : ''} · {job.durationMinutes} minute screen ·{' '}
                {job.questions.length} questions
                {job.language !== 'en' ? ` · ${languageLabel(job.language)}` : ''}
                {job.voiceId ? ' · custom voice' : ''}
              </p>
            </div>
            <Link to="/">Back to roles</Link>
          </div>

          <RoleActions
            job={job}
            formOpen={editing || duplicating}
            canDelete={!candidates.loading && candidates.items.length === 0 && !candidates.hasMore}
            onEdit={() => setEditing(true)}
            onDuplicate={() => setDuplicating(true)}
            onChanged={setJob}
            onDeleted={() => navigate('/', { replace: true })}
          />

          {editing ? (
            <RoleForm
              initial={job}
              onSaved={(saved) => {
                setJob(saved);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : null}

          {duplicating ? (
            <RoleForm
              copyFrom={job}
              onSaved={(saved) => {
                setDuplicating(false);
                navigate(`/jobs/${saved.id}`);
              }}
              onCancel={() => setDuplicating(false)}
            />
          ) : null}

          {job.status === 'open' ? (
            <InviteCandidate jobId={job.id} onInvited={() => void candidates.reload()} />
          ) : (
            <div className="card stack">
              <h2>Invites are paused</h2>
              <p className="muted" style={{ margin: 0 }}>
                {job.status === 'draft'
                  ? 'This role is a draft. Open it for invites to start inviting candidates.'
                  : 'This role is closed, so no new candidates can be invited. Reopen it to invite more.'}
                {candidates.items.length > 0
                  ? ' Candidates already invited can still use their links - revoke a link from the candidate’s page to stop it.'
                  : ''}
              </p>
            </div>
          )}

          <div className="card stack">
            <h2>Question set</h2>
            <QuestionSections job={job} />
          </div>

          <div className="card stack">
            <h2>Candidates</h2>
            {candidates.items.length === 0 ? (
              <p className="muted">
                {candidates.loading ? 'Loading candidates…' : 'No candidates invited yet.'}
              </p>
            ) : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Candidate</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Recommendation</th>
                        <th>Decision</th>
                        <th>Invited</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.items.map(({ interview, overall, recommendation }) => (
                        <tr key={interview.id}>
                          <td>
                            <div>{interview.candidate.name}</div>
                            <div className="muted small">{interview.candidate.email}</div>
                          </td>
                          <td>
                            <StatusBadge status={interview.status} />
                          </td>
                          <td>{overall === undefined ? '—' : `${overall}/100`}</td>
                          <td>
                            {recommendation ? <RecommendationBadge value={recommendation} /> : '—'}
                          </td>
                          <td>
                            {interview.decision ? <DecisionBadge value={interview.decision.outcome} /> : '—'}
                          </td>
                          <td className="muted small">
                            {new Date(interview.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Link to={`/interviews/${interview.id}`}>
                              {NEEDS_NEW_LINK.includes(interview.status) ? 'Reissue' : 'Open'}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {candidates.hasMore ? (
                  <LoadMore loading={candidates.loadingMore} onLoadMore={candidates.loadMore} />
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Edit, duplicate, open, close, reopen and delete.
 *
 * Delete is only offered for a role nobody was invited to - the server refuses
 * the rest - because a role's page is the only way back to its candidates'
 * interviews. Closing is the reversible way to stop invites.
 */
function RoleActions({
  job,
  formOpen,
  canDelete,
  onEdit,
  onDuplicate,
  onChanged,
  onDeleted,
}: {
  job: Job;
  /** An edit or duplicate form is already showing. */
  formOpen: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onChanged: (job: Job) => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<unknown>();

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // Only the status is sent, so nothing else about the role changes.
  const setStatus = (status: JobStatus) =>
    run(async () => {
      onChanged(await api.updateJob(job.id, { status }));
    });

  const remove = () =>
    run(async () => {
      await api.deleteJob(job.id);
      onDeleted();
    });

  return (
    <div className="stack" style={{ gap: '0.6rem' }}>
      <ErrorNotice error={error} />
      <div className="row">
        {formOpen ? null : (
          <>
            <button className="secondary" onClick={onEdit} disabled={busy}>
              Edit role
            </button>
            <button className="secondary" onClick={onDuplicate} disabled={busy}>
              Duplicate
            </button>
          </>
        )}
        {job.status === 'draft' ? (
          <button onClick={() => void setStatus('open')} disabled={busy}>
            Open for invites
          </button>
        ) : null}
        {job.status === 'open' ? (
          <button className="secondary" onClick={() => void setStatus('closed')} disabled={busy}>
            Close role
          </button>
        ) : null}
        {job.status === 'closed' ? (
          <button className="secondary" onClick={() => void setStatus('open')} disabled={busy}>
            Reopen role
          </button>
        ) : null}
        {canDelete && !confirmingDelete ? (
          <button className="danger" onClick={() => setConfirmingDelete(true)} disabled={busy}>
            Delete role
          </button>
        ) : null}
      </div>
      {canDelete && confirmingDelete ? (
        <div className="notice stack" role="alertdialog" aria-label="Delete this role?">
          <span className="small">
            Delete {job.title}? Nobody has been invited to it, so nothing else is lost. This cannot be
            undone.
          </span>
          <div className="row">
            <button className="danger" onClick={() => void remove()} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete role'}
            </button>
            <button className="secondary" onClick={() => setConfirmingDelete(false)} disabled={busy}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The role's questions under their interview sections, in the order the
 * interviewer runs them, with numbering continuous across sections to match the
 * prompt and the scorecard's coverage count.
 */
function QuestionSections({ job }: { job: Job }) {
  const recommended = recommendedStructure(job.seniority);
  let offset = 0;
  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0 }}>
        The interviewer runs a short warm-up and asks about the candidate&apos;s background, then these
        sections in order. Suggested for {job.seniority} roles: {recommended.counts.soft_skill.join('–')}{' '}
        soft skills, {recommended.counts.situational.join('–')} situational,{' '}
        {[...new Set(recommended.counts.technical)].join('–')} technical, about{' '}
        {recommended.durationMinutes} minutes.
      </p>
      {STAGE_ORDER.map((stage) => {
        const questions = job.questions.filter((q) => (q.stage ?? 'technical') === stage);
        if (questions.length === 0) return null;
        const start = offset + 1;
        offset += questions.length;
        return (
          <div key={stage} className="stack" style={{ gap: '0.5rem' }}>
            <h3 className="stage-heading">
              {STAGE_LABELS[stage]} · {questions.length}
            </h3>
            <ol start={start} className="stack" style={{ paddingLeft: '1.2rem', margin: 0 }}>
              {questions.map((question) => (
                <li key={question.id}>
                  {question.text}{' '}
                  <span className="badge">{question.competency}</span>
                  {question.mustAsk ? <span className="badge caution"> must ask</span> : null}
                  {question.followUpHint ? (
                    <div className="small muted">Probe for: {question.followUpHint}</div>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

const RESUME_TYPES: Record<string, NonNullable<ResumeBriefRequest['mediaType']>> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
};

/** Base64 without blowing the call stack on a multi-megabyte file. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function InviteCandidate({ jobId, onInvited }: { jobId: string; onInvited: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [background, setBackground] = useState('');
  const [expiresInHours, setExpiresInHours] = useState(24 * 7);
  const [resumeFile, setResumeFile] = useState<File>();
  const [resumeText, setResumeText] = useState('');
  // Bumped to clear the file input, which cannot be reset through its value.
  const [fileInputKey, setFileInputKey] = useState(0);
  const [flags, setFlags] = useState<string[]>([]);
  const [briefing, setBriefing] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);

  const canBrief = Boolean(resumeFile) || resumeText.trim().length >= CANDIDATE_LIMITS.resumeTextLength[0];

  async function prepareBrief() {
    setError(undefined);
    setBriefing(true);
    try {
      let input: ResumeBriefRequest;
      if (resumeFile) {
        const extension = resumeFile.name.split('.').pop()?.toLowerCase() ?? '';
        const mediaType = RESUME_TYPES[extension];
        if (!mediaType) throw new Error('Upload a PDF, a Word (.docx) document, or a .txt file.');
        if (resumeFile.size > RESUME_MAX_BYTES) throw new Error('Resumes must be 3 MB or smaller.');
        input = { fileName: resumeFile.name, mediaType, data: await fileToBase64(resumeFile) };
      } else {
        input = { text: resumeText.trim() };
      }
      const result = await api.resumeBrief(jobId, input);
      setBackground(result.brief);
      setFlags(result.suspiciousContent);
    } catch (err) {
      setError(err);
    } finally {
      setBriefing(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.inviteCandidate(jobId, {
        candidate: {
          name: name.trim(),
          email: email.trim(),
          background: background.trim() || undefined,
        },
        expiresInHours,
      });
      // Shown once and never stored - the token is the credential for the
      // interview, so losing it means issuing a fresh invite.
      setInviteUrl(result.inviteUrl);
      setName('');
      setEmail('');
      setBackground('');
      setResumeFile(undefined);
      setResumeText('');
      setFlags([]);
      setFileInputKey((key) => key + 1);
      onInvited();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <h2>Invite a candidate</h2>
      <ErrorNotice error={error} />

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="candidate-name">Name</label>
          <input
            id="candidate-name"
            required
            maxLength={CANDIDATE_LIMITS.nameLength}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <label htmlFor="candidate-email">Email</label>
          <input
            id="candidate-email"
            type="email"
            required
            maxLength={CANDIDATE_LIMITS.emailLength}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div style={{ flex: '0 1 150px' }}>
          <label htmlFor="expiry">Link valid (hours)</label>
          <input
            id="expiry"
            type="number"
            min={1}
            max={720}
            value={expiresInHours}
            onChange={(e) => setExpiresInHours(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="stack" style={{ gap: '0.5rem' }}>
        <label htmlFor="resume-file" style={{ marginBottom: 0 }}>
          Resume (optional)
        </label>
        <p className="small muted" style={{ margin: 0 }}>
          Upload a PDF or Word file, or paste the text. It becomes a short, neutral brief for you to
          review below - personal details are left out, and the file itself is not kept.
        </p>
        <div className="row">
          <input
            key={fileInputKey}
            id="resume-file"
            type="file"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            style={{ flex: '1 1 260px' }}
            onChange={(e) => setResumeFile(e.target.files?.[0] ?? undefined)}
          />
          <button
            type="button"
            className="secondary"
            disabled={briefing || !canBrief}
            onClick={() => void prepareBrief()}
          >
            {briefing ? 'Preparing brief…' : 'Prepare brief'}
          </button>
        </div>
        {resumeFile ? null : (
          <textarea
            aria-label="Paste resume text"
            placeholder="…or paste the resume text here"
            maxLength={CANDIDATE_LIMITS.resumeTextLength[1]}
            value={resumeText}
            onChange={(e) => setResumeText(e.target.value)}
          />
        )}
      </div>

      {flags.length > 0 ? (
        <div className="notice error stack" role="alert">
          <strong>This resume contains text aimed at an AI reviewer.</strong>
          <span className="small">
            It has been kept out of the brief, but it is worth a look before you invite this candidate:
          </span>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {flags.map((flag, index) => (
              <li key={index} className="small">
                {flag}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <label htmlFor="background">Candidate brief (optional)</label>
        <textarea
          id="background"
          style={{ minHeight: '11rem' }}
          placeholder="Prepared from the resume above, or write your own notes."
          maxLength={CANDIDATE_LIMITS.backgroundLength}
          value={background}
          onChange={(e) => setBackground(e.target.value)}
        />
        <p className="small muted" style={{ margin: '0.35rem 0 0' }}>
          This is exactly what the interviewer sees. It uses it for follow-up questions and never
          reads it to the candidate.
        </p>
      </div>

      <div className="row">
        <button type="submit" disabled={busy || briefing}>
          {busy ? 'Creating invite…' : 'Create invite link'}
        </button>
      </div>

      {inviteUrl ? <InviteLink url={inviteUrl} /> : null}
    </form>
  );
}
